import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { motion } from "framer-motion";
import { ShieldCheck, Loader2, ShieldX, ArrowUpRight } from "lucide-react";
import { api } from "@/lib/api";

const Kpi = ({ label, value, accent }) => (
  <div className="rounded-xl bg-[#0B101A] border border-white/8 p-6 text-center">
    <div className="font-mono text-[10px] tracking-widest uppercase text-slate-500">{label}</div>
    <div className={`font-display text-3xl mt-2 tabular-nums ${accent}`}>{value}</div>
  </div>
);

export default function ShieldStatus() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    api.get(`/status/shared/${token}`).then((r) => setData(r.data)).catch(() => setNotFound(true));
  }, [token]);

  if (notFound)
    return (
      <div className="min-h-screen bg-[#040914] flex flex-col items-center justify-center gap-4 px-6">
        <ShieldX className="w-10 h-10 text-slate-600" />
        <p data-testid="shield-not-found" className="text-slate-400 text-sm">This shield status page doesn't exist or the link has changed.</p>
        <Link to="/" className="text-cyan-neon text-sm hover:underline">Go to ClickShield AI →</Link>
      </div>
    );

  if (!data)
    return (
      <div className="min-h-screen bg-[#040914] flex items-center justify-center">
        <Loader2 className="w-6 h-6 text-cyan-neon animate-spin" />
      </div>
    );

  return (
    <div className="min-h-screen bg-[#040914] bg-tech-grid relative overflow-hidden">
      <div className="absolute -top-40 left-1/2 -translate-x-1/2 w-[36rem] h-[36rem] rounded-full bg-[#39FF14]/8 blur-[150px] pointer-events-none" />
      <div className="max-w-3xl mx-auto px-6 py-14 relative z-10">
        <div className="flex items-center justify-center gap-2.5 mb-12">
          <ShieldCheck className="w-6 h-6 text-cyan-neon" />
          <span className="font-display text-lg text-white">ClickShield<span className="text-cyan-neon"> AI</span></span>
        </div>

        <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}
          data-testid="shield-status-page" className="text-center">
          <motion.div
            initial={{ scale: 0.8 }} animate={{ scale: 1 }} transition={{ type: "spring", delay: 0.2 }}
            className="inline-flex items-center justify-center w-24 h-24 rounded-full bg-[#39FF14]/10 border border-[#39FF14]/30 mb-6"
          >
            <ShieldCheck className="w-12 h-12 text-[#39FF14]" strokeWidth={1.5} />
          </motion.div>
          <div data-testid="shield-active-badge" className="flex items-center justify-center gap-2 mb-4">
            <span className="w-2.5 h-2.5 rounded-full bg-[#39FF14] animate-pulse-glow" />
            <span className="font-mono text-xs tracking-[0.3em] uppercase text-[#39FF14]">Protection Active</span>
          </div>
          {(data.branding?.brand_name || data.branding?.logo) && (
            <div data-testid="shield-branding" className="flex items-center justify-center gap-3 mb-5">
              {data.branding.logo && (
                <img src={data.branding.logo} alt={data.branding.brand_name || "Brand"}
                  className="w-11 h-11 rounded-lg object-contain bg-white/5 border border-white/10 p-1" />
              )}
              {data.branding.brand_name && (
                <span className="font-mono text-[11px] tracking-[0.25em] uppercase text-slate-300">
                  Protected by <span className="text-white">{data.branding.brand_name}</span>
                </span>
              )}
            </div>
          )}
          <h1 className="font-display text-4xl sm:text-5xl text-white tracking-tight">
            {data.branding?.brand_name
              ? `${data.branding.brand_name}'s campaigns`
              : data.name
              ? `${data.name}'s ad campaigns`
              : "These ad campaigns"}{" "}
            are shielded.
          </h1>
          <p className="mt-4 text-slate-400 font-light">
            Every click on protected pages is fingerprinted and scored in real time. Confirmed bots never make it through.
          </p>

          <div className="grid sm:grid-cols-3 gap-4 mt-10">
            <Kpi label="Fraud blocked (30d)" value={data.blocked_30d.toLocaleString()} accent="text-[#39FF14]" />
            <Kpi label="Ad budget saved" value={`$${Math.round(data.money_saved).toLocaleString()}`} accent="text-cyan-neon" />
            <Kpi label="Block rate" value={`${data.block_rate}%`} accent="text-white" />
          </div>

          {data.platforms.length > 0 && (
            <div className="mt-8 flex flex-wrap items-center justify-center gap-2">
              <span className="font-mono text-[10px] tracking-widest uppercase text-slate-500 mr-1">Protected platforms:</span>
              {data.platforms.map((p) => (
                <span key={p} className="px-3 py-1 rounded-full border border-cyan-neon/25 bg-cyan-neon/5 text-xs text-cyan-neon">{p}</span>
              ))}
            </div>
          )}

          <p className="mt-8 font-mono text-[10px] text-slate-600 tracking-wide">
            Verified by ClickShield AI · {new Date(data.generated_at).toUTCString()}
          </p>

          <Link
            to="/"
            data-testid="shield-cta"
            className="group mt-10 inline-flex items-center gap-2 px-7 py-3.5 rounded-full bg-cyan-neon text-[#040914] font-semibold hover:-translate-y-0.5 hover:glow-cyan transition-all"
          >
            Protect your own campaigns
            <ArrowUpRight className="w-4 h-4 group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform" />
          </Link>
        </motion.div>
      </div>
    </div>
  );
}
